import { useState, useEffect } from "react";

import { AmplitudeVariantValue } from "@simplyk/common";

import { GetExperimentValueProps, useAmplitude } from "./useAmplitude";

import { isProduction } from "@/common/constants/env";
import { setUserProperty } from "@/common/helpers/amplitude";

const getLocalStorageOverride = (flagKey: string): { isActive: boolean; value: AmplitudeVariantValue } | null => {
  if (isProduction || typeof window === "undefined") {
    return null;
  }
  // eslint-disable-next-line zeffy-linter-frontend/no-localstorage
  const override = localStorage.getItem(`ff-override-${flagKey}`);
  if (override !== null) {
    const isActive = override === "true";
    return { isActive, value: isActive ? AmplitudeVariantValue.B : AmplitudeVariantValue.CONTROL };
  }
  return null;
};

type AmplitudeFlagOverrideValue = boolean | AmplitudeVariantValue;
type AmplitudeFlagOverrides = Record<string, AmplitudeFlagOverrideValue>;

const getAmplitudeFlagOverride = (flagKey: string): AmplitudeFlagOverrideValue | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  // Only allow overrides in Cypress E2E tests
  if (!(window as Window & { Cypress?: unknown }).Cypress) {
    return undefined;
  }

  const overrides = (window as Window & { __AMPLITUDE_FLAGS__?: AmplitudeFlagOverrides }).__AMPLITUDE_FLAGS__;

  return overrides?.[flagKey];
};

// Retrieve a feature flag from Amplitude that resolves "b" value to true,
// others to false.
export const useAmplitudeFeatureFlag = ({
  skip,
  fallbackOnFail = false,
  ...rest
}: GetExperimentValueProps & {
  skip?: boolean;
  /** Value to use when Amplitude fails (e.g., due to adblocker). Defaults to false. */
  fallbackOnFail?: boolean;
}) => {
  const { getExperimentValue, isAmplitudeLoading } = useAmplitude();

  const localOverride = getLocalStorageOverride(rest.flagKey);
  const hasOverride = localOverride !== null;
  const evaluationKey = JSON.stringify(rest);
  const [resolvedAssignment, setResolvedAssignment] = useState<{
    evaluationKey: string;
    getExperimentValue: typeof getExperimentValue;
  } | null>(null);

  const [isFlagValueActive, setIsFlagValueActive] = useState<boolean | null>(
    hasOverride ? localOverride.isActive : null
  );
  const [flagValue, setFlagValue] = useState<AmplitudeVariantValue | null>(hasOverride ? localOverride.value : null);
  const [loading, setLoading] = useState(!hasOverride);

  useEffect(() => {
    if (hasOverride) {
      return;
    }

    const cypressOverride = getAmplitudeFlagOverride(rest.flagKey);
    if (cypressOverride !== undefined) {
      const isActiveOverride =
        typeof cypressOverride === "boolean" ? cypressOverride : cypressOverride === AmplitudeVariantValue.B;
      const valueOverride: AmplitudeVariantValue =
        typeof cypressOverride === "boolean"
          ? cypressOverride
            ? AmplitudeVariantValue.B
            : AmplitudeVariantValue.OFF
          : cypressOverride;

      setIsFlagValueActive(isActiveOverride);
      setFlagValue(valueOverride);
      setLoading(false);
      setResolvedAssignment({ evaluationKey, getExperimentValue });
      return;
    }

    if (isAmplitudeLoading) {
      return;
    }

    const abortController = new AbortController();

    const fetchFlag = async () => {
      setLoading(true);

      const flag = await getExperimentValue({
        ...rest,
        signal: abortController.signal,
      }).catch(() => null);
      if (abortController.signal.aborted) {
        return;
      }
      setResolvedAssignment({ evaluationKey, getExperimentValue });

      if (!flag) {
        setIsFlagValueActive(fallbackOnFail);
        setFlagValue(fallbackOnFail ? AmplitudeVariantValue.B : AmplitudeVariantValue.OFF);
        setLoading(false);
        return;
      }

      // eslint-disable-next-line no-console
      console.log(`🅰️ flag exposure: ${rest?.flagKey}=${flag?.value}`);
      setUserProperty(`exp_${rest.flagKey}`, flag?.value || "off");
      setIsFlagValueActive(flag?.value === "b");
      setFlagValue((flag?.value as AmplitudeVariantValue) || null);

      setLoading(false);
    };
    if (!skip) {
      fetchFlag();
    } else {
      setLoading(false);
    }

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getExperimentValue, evaluationKey, skip, isAmplitudeLoading, hasOverride, fallbackOnFail]);

  const hasCurrentAssignment =
    hasOverride ||
    (resolvedAssignment?.evaluationKey === evaluationKey &&
      resolvedAssignment.getExperimentValue === getExperimentValue);
  return {
    isActive: hasCurrentAssignment && Boolean(isFlagValueActive),
    loading: loading || isAmplitudeLoading || (!skip && !hasCurrentAssignment),
    value: hasCurrentAssignment ? flagValue : null,
  };
};
